import { isInternalShareDragData } from "~/lib/internal-share-drag";
import { router } from "@inertiajs/react";
import { dropTargetForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { urlFor } from "~/client";

export type InternalShareTarget =
  | { type: "user"; companyUserId: number }
  | { type: "channel"; conversationId: number }
  | { type: "team"; conversationId: number };

export function useInternalShareDropTarget<T extends HTMLElement>(target: InternalShareTarget) {
  const ref = useRef<T>(null);
  const [isDraggedOver, setIsDraggedOver] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const targetType = target.type;
  const targetKey = target.type === "user" ? target.companyUserId : target.conversationId;

  useEffect(() => {
    const element = ref.current;
    if (!element) {
      return;
    }

    return dropTargetForElements({
      element,
      canDrop: ({ source }) => isInternalShareDragData(source.data),
      onDragEnter: () => setIsDraggedOver(true),
      onDragLeave: () => setIsDraggedOver(false),
      onDrop: ({ source }) => {
        setIsDraggedOver(false);
        if (!isInternalShareDragData(source.data) || isSharing) {
          return;
        }

        const isConversation = targetType === "channel" || targetType === "team";
        const successMessage =
          targetType === "channel"
            ? "Compartido en el canal."
            : targetType === "team"
              ? "Compartido con el equipo."
              : "Compartido por mensaje directo.";
        const errorMessage =
          targetType === "channel"
            ? "No se pudo compartir en el canal."
            : targetType === "team"
              ? "No se pudo compartir con el equipo."
              : "No se pudo compartir por mensaje directo.";
        setIsSharing(true);
        router.post(
          urlFor("chat.internal-shares.store"),
          {
            resourceType: source.data.resourceType,
            resourceId: Number(source.data.resourceId),
            ...(isConversation
              ? { targetConversationId: targetKey }
              : { targetCompanyUserId: targetKey }),
          },
          {
            preserveScroll: true,
            onSuccess: () => {
              toast.success(successMessage);
            },
            onError: () => {
              toast.error(errorMessage);
            },
            onFinish: () => setIsSharing(false),
          },
        );
      },
    });
  }, [isSharing, targetType, targetKey]);

  return { ref, isDraggedOver, isSharing };
}
